import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import { useRegisterFieldEditModalHeaderAction } from '../../../contexts/FieldEditModalHeaderActionContext';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useAiGeneratedFields } from '../../../hooks/queries/useAiGeneratedFields';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';

import { getDataToSave, hasAiFieldsInGroups, restoreAiAmenitySelections } from '../../../utils/amenitiesUtils';

import type { ChangeEvent } from 'react';
import type { GetUserPropertyDetailsAmenitiesGroupWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const buildInitialState = (interior: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>) => {
  const categorizedAmenitiesKeys: Record<string, Array<string>> = { checkboxes: [] };
  const initialInputs: Record<string, boolean | string> = interior.reduce(
    (a, { category, isUniqueSelection, values }) => {
      if (isUniqueSelection) {
        categorizedAmenitiesKeys[category] = [];
      }
      values.forEach(({ fieldName, isSelected }) => {
        if (!isUniqueSelection) {
          a[fieldName] = Boolean(isSelected);
        } else if (isSelected) {
          a[category] = fieldName;
        }
        if (isUniqueSelection) {
          categorizedAmenitiesKeys[category].push(fieldName);
        } else {
          categorizedAmenitiesKeys.checkboxes.push(fieldName);
        }
      });
      return a;
    },
    {} as Record<string, boolean | string>,
  );
  return { initialInputs, categorizedAmenitiesKeys };
};

const AmenitiesInteriorFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const interior = listingDetails?.amenities?.interior ?? [];

  const { propertyFields } = useAiGeneratedFields();
  const aiRevertFields = propertyFields?.amenityFields;

  // The saved listing is the only source. Generation writes the draft onto the listing, so there is
  // nothing to merge in here.
  const { initialInputs, categorizedAmenitiesKeys } = useMemo(
    () => buildInitialState(interior),
    [listingAlias, interior],
  );

  const [inputs, setInputs] = useState<Record<string, boolean | string>>(initialInputs);

  useEffect(() => {
    setInputs(initialInputs);
  }, [initialInputs]);

  const updateMutation = useMultiEditMutation({
    meta: { context: { api: 'multi', details: 'AmenitiesInteriorFormField' } },
  });

  const field = useFieldSave({
    fieldName: 'amenities',
    initialValue: JSON.stringify(initialInputs),
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (jsonValue) => ({
      getParams: { listingAlias },
      postData: getDataToSave({
        inputs: JSON.parse(jsonValue) as Record<string, boolean | string>,
        categorizedAmenitiesKeys,
        listingAlias,
      }),
    }),
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const valueToSave = categorizedAmenitiesKeys[name] ? value : value === 'true';
    const newInputs = { ...inputs, [name]: valueToSave };
    setInputs(newInputs);
    field.onChange(JSON.stringify(newInputs));
  };

  const aiRestoredInputs = useMemo(
    () => restoreAiAmenitySelections({ inputs, groups: interior, aiAmenityFields: aiRevertFields }),
    [inputs, interior, aiRevertFields],
  );

  // Offered only once the section differs from the draft, matching the description field.
  const canRevertAiSelections =
    hasAiFieldsInGroups(interior, aiRevertFields) && JSON.stringify(aiRestoredInputs) !== JSON.stringify(inputs);

  const handleRevert = () => {
    setInputs(aiRestoredInputs);
    field.onChange(JSON.stringify(aiRestoredInputs));
  };

  useRegisterFieldEditModalHeaderAction(
    canRevertAiSelections ? { ariaLabel: 'Revert AI-selected interior amenities', onAction: handleRevert } : undefined,
  );

  return (
    <div data-test="AmenitiesInteriorFormField">
      {field.error && (
        <Alert tone="critical" css={{ marginBottom: '400' }}>
          {field.error}
        </Alert>
      )}
      <Box css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}>
        {interior.map(({ category, isEditable, isRequired, isUniqueSelection, values }) => (
          <Box
            key={`${category}FieldSetContainer`}
            css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
          >
            <FieldSet disabled={!isEditable} required={isRequired && isEditable} legend={<Legend>{category}</Legend>}>
              {values.map(({ displayName, fieldName }) => {
                const ControlInput = isUniqueSelection ? Radio : Checkbox;
                return (
                  <LabeledControl
                    key={displayName}
                    label={<Label>{displayName}</Label>}
                    control={
                      <ControlInput
                        name={isUniqueSelection ? category : fieldName}
                        value={isUniqueSelection ? fieldName : String(!inputs[fieldName])}
                        checked={isUniqueSelection ? inputs[category] === fieldName : Boolean(inputs[fieldName])}
                        onChange={handleChange}
                      />
                    }
                  />
                );
              })}
            </FieldSet>
          </Box>
        ))}
      </Box>
    </div>
  );
};

export default AmenitiesInteriorFormField;
